/* eslint-disable complexity */
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '~/components/text-section/TextSection';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { useEarningsInsightsFilter } from '~/react-query-hooks/insights/useEarningsInsightsFilter';
import { useGetEarningsInsightRecap } from '~/react-query-hooks/insights/useGetEarningsInsightRecap';
import { ScheduleDemoBanner } from '~/views/stream/components/schedule-demo-banner/ScheduleDemoBanner';
import { MarketReactionSection } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/earnings-recap/components/market-reaction-section/MarketReactionSection';

import { useEarningsInsightDrawer } from '../../useEarningsInsightDrawer';
import { BullBearPoints } from '../components/bull-bear-points/BullBearPoints';
import { Sidebar } from '../components/side-bar/Sidebar';
import { useSidebarItems } from '../components/side-bar/useSidebarItems';
import * as Shared from '../EarningsInsightWidget.styles';
import { DocumentsAndCitations } from './components/documents-and-citations/DocumentsAndCitations';
import { EarningsHighlightsTable } from './components/earnings-highlights-table/EarningsHighlightsTable';
import { KpisTable } from './components/KpisTable/KpisTable';
import { ManagementCommentarySection } from './components/management-commentary-section/ManagementCommentarySection';
import { RecapOverview } from './components/recap-overview/RecapOverview';
import * as S from './EarningsRecap.styles';

export interface EarningsRecapProps {
  expandCitations: boolean;
}

export const EarningsRecap = ({ expandCitations }: EarningsRecapProps) => {
  const { t } = useTranslation(['earnings']);
  const { earningsId, activeEntityTag } = useEarningsInsightDrawer();
  const containerRef = useRef<HTMLDivElement>(null);
  const { recapItems } = useSidebarItems();

  const {
    data: entityData,
    isFetching: isEntityFetching,
    isError: isEntityError,
    error: entityError,
  } = useEntity(activeEntityTag ?? '');

  const {
    data: earningsInsightsData,
    isFetching: isEarningsInsightsFetching,
    isError: isEarningsInsightsError,
    error: earningsInsightsError,
  } = useEarningsInsightsFilter({
    body: {
      earnings_ids: [earningsId ?? ''],
    },
    isEnabled: !!earningsId,
  });

  const {
    data: earningsData,
    isFetching: isEarningsFetching,
    isError: isEarningsError,
    error: earningsError,
  } = useGetEarningById(earningsId ?? '');

  const { data: earningsRecapData, isFetching: isEarningsRecapFetching } =
    useGetEarningsInsightRecap(
      earningsInsightsData?.find(insight => insight.type === 'recap')?.id ?? ''
    );

  const isLoading =
    isEarningsFetching ||
    isEarningsRecapFetching ||
    isEarningsInsightsFetching ||
    isEntityFetching;

  const isError = isEarningsError || isEarningsInsightsError || isEntityError;

  if (isLoading) {
    return <Shared.StyledLoader />;
  }

  if (isError || !entityData?.data) {
    throw new Error(
      earningsError?.message ||
        earningsInsightsError?.message ||
        entityError?.message
    );
  }

  const isReported = earningsData?.reported;

  if (!isReported) {
    return (
      <Shared.Container>
        <Shared.EmptyWrapper>
          <Shared.StyledImg
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <Shared.EmptyTitle>
            {t('earnings:earningsInsight.drawer.earningsRecap.emptyTitle')}
          </Shared.EmptyTitle>
        </Shared.EmptyWrapper>
      </Shared.Container>
    );
  }

  const entity = entityData.data;

  if (!earningsRecapData) {
    return (
      <S.Root>
        <S.TableWrapper>
          <EarningsHighlightsTable earningsData={earningsData} />
        </S.TableWrapper>
        <Shared.EmptyWrapper>
          <Shared.StyledImg
            alt=""
            src="/app/static/images/empty-state/radio-telescope.png"
            height={130}
            width={146}
          />
          <Shared.EmptyTitle>
            {t(
              'earnings:earningsInsight.drawer.earningsRecap.earningsAreBeingAnalyzedOnQuarterYear',
              {
                fiscalPeriod: earningsData.fiscal_period,
                year: earningsData.fiscal_year,
              }
            )}
          </Shared.EmptyTitle>
        </Shared.EmptyWrapper>
      </S.Root>
    );
  }

  return (
    <S.ContentWrapper>
      <S.Separator>
        <S.LeftContainer>
          <S.ScrollableContainer ref={containerRef} data-print-scrollable>
            <S.Section ref={recapItems[0].ref} data-testid="overview">
              <RecapOverview
                title={earningsRecapData.title}
                earningsData={earningsData}
                recapData={earningsRecapData}
              />
            </S.Section>
            <S.Section ref={recapItems[1].ref} data-testid="market-reaction">
              <S.HistoricalReactionWrapper>
                <MarketReactionSection
                  entity={entity}
                  earningsId={earningsId ?? ''}
                  reportingDate={earningsData.reporting_date}
                />
              </S.HistoricalReactionWrapper>
            </S.Section>
            <S.Section ref={recapItems[2].ref} data-testid="kpis-in-focus">
              <KpisTable kpisData={earningsRecapData.kpis} />
            </S.Section>
            <S.Section
              ref={recapItems[3].ref}
              data-testid="management-commentary"
            >
              <ManagementCommentarySection
                managementInsights={earningsRecapData.insights}
                quotes={earningsRecapData.quotes}
                createdAt={earningsRecapData.created_at}
              />
            </S.Section>
            <S.Section ref={recapItems[4].ref} data-testid="thematic-focus">
              <ThematicFocus
                themes={earningsRecapData.themes}
                otherThemes={earningsRecapData.other_themes}
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.thematicFocusRecap'
                )}
                withPill
              />
            </S.Section>
            <S.Section
              ref={recapItems[5].ref}
              data-testid="related-companies-impact"
            >
              <ErrorBoundaryWrapper>
                <ImpactedCompanies
                  impactedCompanies={earningsRecapData.impacted_companies}
                  variant="secondary"
                  title={t('earnings:earningsInsight.drawer.impactedCompanies')}
                  insightCreatedAt={earningsRecapData.created_at}
                />
              </ErrorBoundaryWrapper>
            </S.Section>
            <S.Section ref={recapItems[6].ref} data-testid="peer-comparison">
              <TextSection
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.peerComparison'
                )}
                variant="primary"
                useBulletPoints
                textContent={earningsRecapData.peer_comparison_sector_context}
              />
            </S.Section>
            <S.Section ref={recapItems[7].ref} data-testid="analyst-and-street">
              <BullBearPoints
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.analystAndStreet'
                )}
                data={earningsRecapData.analyst_and_street_reaction}
                variant="primary"
                useBulletPoints
              />
            </S.Section>
            <S.Section
              ref={recapItems[8].ref}
              data-testid="risk-assessment-update"
            >
              <TextSection
                title={t(
                  'earningsInsight.drawer.earningsRecap.items.riskAssessmentUpdate'
                )}
                variant="primary"
                useBulletPoints
                textContent={[
                  ...earningsRecapData.risk_assessment_update.assessments,
                  <span key="upcoming-catalysts">
                    <S.UpcomingCatalysts>
                      {t(
                        'earningsInsight.drawer.earningsRecap.upcomingCatalysts'
                      )}
                    </S.UpcomingCatalysts>
                    <span>
                      {
                        earningsRecapData.risk_assessment_update
                          .upcoming_catalysts
                      }
                    </span>
                  </span>,
                ]}
              />
            </S.Section>
            <S.Section data-testid="citations">
              <DocumentsAndCitations
                earningsRecapData={earningsRecapData}
                expandCitations={expandCitations}
              />
            </S.Section>
            <ScheduleDemoBanner />
          </S.ScrollableContainer>
        </S.LeftContainer>
        <Sidebar containerRef={containerRef} items={recapItems} />
      </S.Separator>
    </S.ContentWrapper>
  );
};
